import React, { useState } from 'react';
import { momentForDomain } from '@common/services/moment-shadow';
import { Link, Drawer, ErrorHandler } from '@common/components';
import { Button, Form, FormInstance, Space } from 'antd';
import { useNavigate, useMutation, useQuery } from '@common/hooks';
import { ShiftRequestDetailsBody } from './_shift-request-details-body';
import {
  SHIFT_EDIT_BUTTON,
  SHIFT_GAP_TAGS_QUERY,
  UPSERT_WORKFORCE_GROUP_SHIFT_GAP_OVERRIDES,
} from '@scheduling/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import { buildDialogUrl } from '@common/services';
import { useCurrentMembership } from '@identity/hooks';

const moment = momentForDomain('scheduling', 'shifts');

export function ShiftRequestDetailsDrawer({
  open,
  onClose,
  type,
  shiftId = '',
}: {
  open: boolean;
  onClose: () => void;
  type?: string;
  shiftId?: string;
}) {
  const [matchBreakdownEditMode, setMatchBreakdownEditMode] = useState(false);
  const [wfgOverrideRates, setWfgOverrideRates] = useState<
    {
      workforceGroupId: string;
      rate: number;
      moneyAmountCents: number | null;
    }[]
  >([]);
  const [shiftEditMode, setShiftEditMode] = useState(false);
  const [shiftGapEditForm] = Form.useForm();
  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();
  const { data: tagsData, error: tagsError } = useQuery(SHIFT_GAP_TAGS_QUERY, {
    variables: { includeAllUnits: true },
  });

  const { data: shiftEditButtonData, error: shiftEditButtonError } = useQuery(
    SHIFT_EDIT_BUTTON,
    {
      skip: !shiftId,
      variables: {
        id: shiftId,
      },
    }
  );

  const error = tagsError || shiftEditButtonError;
  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="ShiftRequestDetailsDrawer"
        messageType="contextual"
      />
    );
  }

  const canEditShiftGaps = currentMembership.canEditShiftGaps;
  const canSendShift = currentMembership.canShowShiftTargeting;

  const resetMatchBreakdownEditMode = () => {
    setMatchBreakdownEditMode(false);
    setWfgOverrideRates([]);
  };

  const resetShiftEditMode = () => {
    setShiftEditMode(false);
    shiftGapEditForm.resetFields();
  };

  function buildShiftsCalendarDetailsDrawerRoute(date: string) {
    return buildDialogUrl('shifts-calendar-details-drawer', {
      props: { date },
      overlap: true,
    });
  }

  const onUserClose = () => {
    resetMatchBreakdownEditMode();
    resetShiftEditMode();

    if (shiftEditButtonData && type === 'calendar') {
      const date = moment
        .parseZone(shiftEditButtonData.shiftGap.startDatetime)
        .tz(shiftEditButtonData.shiftGap.timeZone)
        .format('YYYY-MM-DD');

      navigate(buildShiftsCalendarDetailsDrawerRoute(date));
    }

    onClose();
  };

  const canEditShift = shiftEditButtonData?.shiftGap.canAddOpenings.value;
  const canEditRates = shiftEditButtonData?.shiftGap.canEditRates.value;
  const isPastShift = moment().isAfter(
    shiftEditButtonData?.shiftGap?.startDatetime
  );
  const isOpen = shiftEditButtonData?.shiftGap?.state === 'open';

  const canEditShiftDetails = canEditShiftGaps && canEditShift;
  const tagsEnabled = canEditShiftGaps && !!tagsData?.shiftGapTags?.length;
  const showCanSend = canSendShift && !isPastShift && isOpen;

  const sendShiftDrawerUrl = buildDialogUrl('send-shift-drawer', {
    overlap: true,
    props: {
      shiftId,
      type: type || '',
      from: 'list',
    },
  });

  return (
    <>
      <Drawer
        open={open}
        className="shift-request-details-drawer"
        title="Shift Details"
        onClose={onUserClose}
        footer={
          (canEditShiftDetails ||
            canEditRates ||
            showCanSend ||
            tagsEnabled) && (
            <Space>
              {(canEditShiftDetails || canEditRates || tagsEnabled) && (
                <Footer
                  shiftGapId={shiftId}
                  wfgOverrideRates={wfgOverrideRates}
                  matchBreakdownEditMode={matchBreakdownEditMode}
                  resetMatchBreakdownEditMode={resetMatchBreakdownEditMode}
                  shiftEditMode={shiftEditMode}
                  resetShiftEditMode={resetShiftEditMode}
                  shiftGapEditForm={shiftGapEditForm}
                />
              )}
              {!shiftEditMode && !matchBreakdownEditMode && showCanSend && (
                <Link to={sendShiftDrawerUrl}>
                  <Button size="large" type="primary">
                    Send Shift
                  </Button>
                </Link>
              )}
            </Space>
          )
        }
        size="lg"
      >
        {shiftId && (
          <ShiftRequestDetailsBody
            shiftId={shiftId}
            matchBreakdownEditMode={matchBreakdownEditMode}
            setMatchBreakdownEditMode={setMatchBreakdownEditMode}
            wfgOverrideRates={wfgOverrideRates}
            setWfgOverrideRates={setWfgOverrideRates}
            shiftEditMode={shiftEditMode}
            setShiftEditMode={setShiftEditMode}
            shiftGapEditForm={shiftGapEditForm}
            canEditShiftDetails={!!canEditShiftDetails}
            canEditRates={!!canEditRates}
            tagsEnabled={!!tagsEnabled}
            canSendShift={!!canSendShift}
          />
        )}
      </Drawer>
    </>
  );
}

function Footer({
  shiftGapId,
  wfgOverrideRates,
  matchBreakdownEditMode,
  resetMatchBreakdownEditMode,
  shiftEditMode,
  shiftGapEditForm,
  resetShiftEditMode,
}: {
  shiftGapId: string;
  wfgOverrideRates: {
    workforceGroupId: string;
    rate: number;
    moneyAmountCents: number | null;
  }[];
  matchBreakdownEditMode: boolean;
  resetMatchBreakdownEditMode: () => void;
  shiftEditMode: boolean;
  shiftGapEditForm: FormInstance;
  resetShiftEditMode: () => void;
}) {
  const [overrideWfg] = useMutation(
    UPSERT_WORKFORCE_GROUP_SHIFT_GAP_OVERRIDES,
    {
      onCompleted: () => {
        resetMatchBreakdownEditMode();
        createMessage('success', 'Rates successfully updated.');
      },
      onError: () => {
        createMessage(
          'error',
          'Rate update failed. Please try again or contact your hiring partner.'
        );
      },
      refetchQueries: [
        'WorkforceGroupMatchBreakdown',
        'SpearfishableWorkersByShiftGap',
        'SpearfishableGroupsByShiftGap',
        'ShiftGapDetails',
      ],
    }
  );

  const saveRatesOverrides = () => {
    overrideWfg({
      variables: {
        shiftGapId: shiftGapId,
        input: wfgOverrideRates.map(
          ({
            workforceGroupId,
            rate,
            moneyAmountCents,
          }: {
            workforceGroupId: string;
            rate: number;
            moneyAmountCents: number | null;
          }) => {
            return {
              workforceGroupId: workforceGroupId,
              rate: rate,
              moneyAmountCents: moneyAmountCents,
            };
          }
        ),
      },
    });
  };

  return (
    <>
      {matchBreakdownEditMode ? (
        <Space>
          <Button size="large" onClick={resetMatchBreakdownEditMode}>
            Cancel
          </Button>

          <Button size="large" type="primary" onClick={saveRatesOverrides}>
            Save
          </Button>
        </Space>
      ) : (
        shiftEditMode && (
          <Space>
            <Button size="large" onClick={resetShiftEditMode}>
              Cancel
            </Button>

            <Button
              size="large"
              type="primary"
              onClick={shiftGapEditForm.submit}
            >
              Save
            </Button>
          </Space>
        )
      )}
    </>
  );
}
